import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { store } from 'redux/store';
import { setCurrentTab } from 'redux/reducers/fluxAutomaticLayoutReducer';
import { close, openRight } from 'redux/reducers/sidebarsReducer';
import { format, parseISO, subDays } from 'date-fns';
import DatePicker from 'components/DatePicker';
import MonthYearPicker from 'components/Flux/Automatic/Mobile/MonthYearPicker';
import useIsMobile from 'utils/hooks/useIsMobile';
import {
  useVobsDadvazAvailableQuery,
  useVobsDadvazDownloadMutation,
  useVobsPrevsAvailableQuery,
  useVobsPrevsDownloadMutation,
} from 'services/api/products/flux';
import DownloadCard from './DownloadCard';
import { VOBS_PRODUCTS, DADVAZ_MIN_DATE, MONTHS_PT } from './vobs.constants';
import './index.scss';

export default function Vobs() {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  const [dadvazDate, setDadvazDate] = useState<Date | null>(null);
  const [prevsSemMonth, setPrevsSemMonth] = useState<number | null>(null);
  const [prevsSemYear, setPrevsSemYear] = useState<number>(new Date().getFullYear());
  const [prevsMenMonth, setPrevsMenMonth] = useState<number | null>(null);
  const [prevsMenYear, setPrevsMenYear] = useState<number>(new Date().getFullYear());

  useEffect(() => {
    store.dispatch(setCurrentTab('vobs'));
    dispatch(close());
    return () => { dispatch(openRight()); };
  }, [dispatch]);

  // API: intervalo de datas dadvaz
  const { data: dadvazRange } = useVobsDadvazAvailableQuery();
  const dadvazMaxDate = dadvazRange?.data_fim
    ? parseISO(dadvazRange.data_fim)
    : subDays(new Date(), 1);
  const dadvazIntervals = dadvazRange?.data_fim
    ? [{ start: DADVAZ_MIN_DATE, end: dadvazMaxDate }]
    : undefined;

  // API: meses disponíveis para prevs (refetch ao mudar ano)
  const { data: prevsSemAvailable } = useVobsPrevsAvailableQuery({ ano: prevsSemYear });
  const { data: prevsMenAvailable } = useVobsPrevsAvailableQuery({ ano: prevsMenYear });

  // Meses habilitados (extrair número do mês de 'AAAA-MM')
  const semEnabledMonths = new Set(
    (prevsSemAvailable?.meses_disponiveis?.semanal ?? []).map(
      (m: string) => parseInt(m.split('-')[1], 10) - 1,
    ),
  );
  const menEnabledMonths = new Set(
    (prevsMenAvailable?.meses_disponiveis?.mensal ?? []).map(
      (m: string) => parseInt(m.split('-')[1], 10) - 1,
    ),
  );

  // API: mutations de download
  const [triggerDadvazDownload, { isLoading: isDadvazLoading }] = useVobsDadvazDownloadMutation();
  const [triggerPrevsDownload, { isLoading: isPrevsLoading }] = useVobsPrevsDownloadMutation();

  // Nomes de arquivo reativos
  const dadvazFileName = dadvazDate
    ? `dadvaz_from_vobs_${format(dadvazDate, 'yyyyMMdd')}.dat`
    : null;

  const prevsSemFileName = prevsSemMonth !== null
    ? `PREVS.FROM-VOBS.${prevsSemYear}${String(prevsSemMonth + 1).padStart(2, '0')}.REVF`
    : null;

  const prevsMenFileName = prevsMenMonth !== null
    ? `PREVS.MENSAL.FROM-VOBS.${prevsMenYear}${String(prevsMenMonth + 1).padStart(2, '0')}.REVF`
    : null;

  // Handlers de download com feedback de erro
  const handleDadvazDownload = async () => {
    if (!dadvazDate) return;
    const result = await triggerDadvazDownload({ data: format(dadvazDate, 'yyyy-MM-dd') });
    if ('data' in result && result.data?.url) {
      window.open(result.data.url, '_blank');
    } else {
      // eslint-disable-next-line no-alert
      alert('Arquivo não encontrado para a data selecionada.');
    }
  };

  const handlePrevsDownload = async (tipo: 'semanal' | 'mensal') => {
    const month = tipo === 'semanal' ? prevsSemMonth : prevsMenMonth;
    const year = tipo === 'semanal' ? prevsSemYear : prevsMenYear;
    if (month === null) return;
    const result = await triggerPrevsDownload({ ano: year, mes: month + 1, tipo });
    if ('data' in result && result.data?.url) {
      window.open(result.data.url, '_blank');
    } else {
      // eslint-disable-next-line no-alert
      alert(`Arquivo ${tipo} não encontrado para o período selecionado.`);
    }
  };

  // Anos disponíveis (2018 até ano atual)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2018 + 1 }, (_, i) => 2018 + i);

  // Componente de selects mês/ano com meses filtrados por disponibilidade
  const renderMonthYearSelector = (
    month: number | null,
    year: number,
    enabledMonths: Set<number>,
    onMonthChange: (m: number) => void,
    onYearChange: (y: number) => void,
    onYearReset: () => void,
    contextLabel: string,
  ) => (
    <div>
      <p className="vobs-card-selector-label">Selecione mês / ano</p>
      {isMobile ? (
        <MonthYearPicker
          month={month}
          year={year}
          years={years}
          enabledMonths={enabledMonths}
          onMonthChange={onMonthChange}
          onYearChange={(y) => { onYearChange(y); onYearReset(); }}
          contextLabel={contextLabel}
        />
      ) : (
        <div className="vobs-card-month-selects">
          <select
            value={month ?? ''}
            onChange={(e) => onMonthChange(Number(e.target.value))}
          >
            <option value="" disabled>Mês</option>
            {MONTHS_PT.map((name, i) => (
              <option key={name} value={i} disabled={!enabledMonths.has(i)}>
                {name}
              </option>
            ))}
          </select>
          <select
            value={year}
            onChange={(e) => { onYearChange(Number(e.target.value)); onYearReset(); }}
          >
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );

  return (
    <div className="vobs-page">
      <div className="vobs-list">
        {/* Card DADVAZ */}
        <DownloadCard
          title={VOBS_PRODUCTS.dadvaz.title}
          subtitle={VOBS_PRODUCTS.dadvaz.subtitle}
          description={VOBS_PRODUCTS.dadvaz.description}
          color={VOBS_PRODUCTS.dadvaz.color}
          icon={VOBS_PRODUCTS.dadvaz.icon}
          granularity={VOBS_PRODUCTS.dadvaz.granularity}
          format={VOBS_PRODUCTS.dadvaz.format}
          selector={(
            <div>
              <p className="vobs-card-selector-label">Selecione a data</p>
              <div className="vobs-datepicker-wrapper">
                <DatePicker
                  selected={dadvazDate}
                  onChange={(date) => setDadvazDate(date as Date | null)}
                  includeDateIntervals={dadvazIntervals}
                  maxDate={dadvazMaxDate}
                  minDate={DADVAZ_MIN_DATE}
                  placeholderText="Selecione uma data"
                  dateFormat="dd/MM/yyyy"
                  showIcon
                  toggleCalendarOnIconClick
                />
              </div>
            </div>
          )}
          fileName={dadvazFileName}
          onDownload={handleDadvazDownload}
          isLoading={isDadvazLoading}
          disabled={!dadvazDate}
        />

        {/* Card PREVS Semanal */}
        <DownloadCard
          title={VOBS_PRODUCTS.prevs_sem.title}
          subtitle={VOBS_PRODUCTS.prevs_sem.subtitle}
          description={VOBS_PRODUCTS.prevs_sem.description}
          color={VOBS_PRODUCTS.prevs_sem.color}
          icon={VOBS_PRODUCTS.prevs_sem.icon}
          granularity={VOBS_PRODUCTS.prevs_sem.granularity}
          format={VOBS_PRODUCTS.prevs_sem.format}
          selector={renderMonthYearSelector(
            prevsSemMonth,
            prevsSemYear,
            semEnabledMonths,
            setPrevsSemMonth,
            setPrevsSemYear,
            () => setPrevsSemMonth(null),
            VOBS_PRODUCTS.prevs_sem.title,
          )}
          fileName={prevsSemFileName}
          onDownload={() => handlePrevsDownload('semanal')}
          isLoading={isPrevsLoading}
          disabled={prevsSemMonth === null}
        />

        {/* Card PREVS Mensal */}
        <DownloadCard
          title={VOBS_PRODUCTS.prevs_men.title}
          subtitle={VOBS_PRODUCTS.prevs_men.subtitle}
          description={VOBS_PRODUCTS.prevs_men.description}
          color={VOBS_PRODUCTS.prevs_men.color}
          icon={VOBS_PRODUCTS.prevs_men.icon}
          granularity={VOBS_PRODUCTS.prevs_men.granularity}
          format={VOBS_PRODUCTS.prevs_men.format}
          selector={renderMonthYearSelector(
            prevsMenMonth,
            prevsMenYear,
            menEnabledMonths,
            setPrevsMenMonth,
            setPrevsMenYear,
            () => setPrevsMenMonth(null),
            VOBS_PRODUCTS.prevs_men.title,
          )}
          fileName={prevsMenFileName}
          onDownload={() => handlePrevsDownload('mensal')}
          isLoading={isPrevsLoading}
          disabled={prevsMenMonth === null}
        />
      </div>
    </div>
  );
}
